/**
 * Thin client for the `/api/billing/*` HTTP endpoints exposed by the
 * game server. The WebSocket connection stays focused on real-time
 * gameplay; everything billing-related (Stripe Checkout / Customer
 * Portal / status polling) goes through plain HTTPS so it can be
 * opened in a separate tab without disturbing the live session.
 *
 * The session is identified by the `x-billing-token` header. The token
 * is the opaque `playerId` returned by `AuthResponse`; the server
 * resolves it back to the live `PlayerSession` and looks the account
 * up from there. Tokens are scoped to the active WebSocket — once the
 * session is gone, the corresponding HTTP requests start returning
 * `401 unauthorized` and the UI is expected to ask the user to
 * re-authenticate.
 */

export type BillingPlan = 'monthly' | 'yearly';

export interface BillingStatusResponse {
  active: boolean;
  currentPeriodEnd: number | null;
  /**
   * `false` when the server has Stripe wired in code but
   * `VIRAL_SPACE_BILLING_ENABLED` is not set in the environment.
   * Lets the UI surface "billing currently disabled" instead of
   * pretending checkout will work.
   */
  billingEnabled: boolean;
}

export interface BillingCheckoutResponse {
  url: string;
  sessionId: string;
  plan: BillingPlan;
}

export interface BillingPortalResponse {
  url: string;
}

/**
 * Convert the live WebSocket URL (`ws://host:port` or `wss://host/ws`)
 * to the HTTP base the billing endpoints sit on. Mirrors the
 * `wsUrl.replace(/^ws/, 'http').replace(/\/ws$/, '')` trick used by
 * the locale loader so both code paths behave identically across
 * dev, mobile, and reverse-proxied production deployments.
 */
export function billingHttpBase(wsUrl: string): string {
  return wsUrl.replace(/^ws/i, 'http').replace(/\/ws$/i, '');
}

async function request<T>(
  base: string,
  path: string,
  token: string | null,
  init: { method?: 'GET' | 'POST'; body?: unknown } = {},
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) headers['x-billing-token'] = token;
  const res = await fetch(`${base}${path}`, {
    method: init.method ?? 'GET',
    headers,
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
    credentials: 'omit',
    cache: 'no-store',
  });
  let payload: unknown = null;
  const text = await res.text();
  if (text) {
    try { payload = JSON.parse(text); } catch { payload = { raw: text }; }
  }
  if (!res.ok) {
    const err = (payload as { error?: string } | null)?.error
      ?? `http_${res.status}`;
    throw new BillingError(err, res.status, payload);
  }
  return payload as T;
}

export class BillingError extends Error {
  readonly status: number;
  readonly payload: unknown;
  constructor(code: string, status: number, payload: unknown) {
    super(code);
    this.name = 'BillingError';
    this.status = status;
    this.payload = payload;
  }
}

export class BillingClient {
  private readonly base: string;
  private token: string | null;

  constructor(serverUrl: string, token: string | null) {
    this.base = billingHttpBase(serverUrl);
    this.token = token;
  }

  setToken(token: string | null): void {
    this.token = token;
  }

  hasToken(): boolean {
    return !!this.token;
  }

  status(): Promise<BillingStatusResponse> {
    return request<BillingStatusResponse>(this.base, '/api/billing/status', this.token);
  }

  checkout(plan: BillingPlan, returnUrl?: string): Promise<BillingCheckoutResponse> {
    return request<BillingCheckoutResponse>(
      this.base,
      '/api/billing/checkout',
      this.token,
      { method: 'POST', body: { plan, returnUrl } },
    );
  }

  portal(returnUrl?: string): Promise<BillingPortalResponse> {
    return request<BillingPortalResponse>(
      this.base,
      '/api/billing/portal',
      this.token,
      { method: 'POST', body: { returnUrl } },
    );
  }
}
